import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppState } from "../../appSlice";
import { clearDeals } from "../../features/dealsList/dealsListSlice";
import {
  updateMainFilterFromUrl,
} from "../../features/mainFilter/mainFilterSlice";
import { updateSecondaryFilterFromUrl } from "../../features/secondaryFilter/secondaryFilterSlice";
import { getFormattedWhen, getParamFromUrl, getParamsFromUrl, isEntryFlow, removeParamFromUrl } from '../../utilities/urlResolver';
import usePreviousValue from "../usePreviousValue";
import {
  fetchHotelById,
  fetchStaticHotelById,
  setShowLoader,
} from '../../features/search/searchSlice';
import { selectFiltersLoaded } from '../../features/mainFilter/selectors';
import { updateSearch } from "../../features/mainFilter/searchSlice";
import abTesting from "../../services/abTesting";

export default function useGetDataFromUrl() {
  const dispatch = useDispatch();
  const location = useLocation();
  const firstLoad = useRef(true);
  const showResults = useSelector((state) => state.app.showResults);
  const lastChangedFilter = useSelector((state) => state.app.lastChangedFilter);
  const previousLocationHash = usePreviousValue(location.hash);
  const filtersReady = useSelector(selectFiltersLoaded);

  useEffect(() => {
    if (firstLoad.current) {
      if (!!abTesting.noEntryFlow && (isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome'))) {
        // disable entry flow if there is noEntryFlow cookie (remove all pr params from url)
        const prParams = Object.keys(getParamsFromUrl()).filter(prm => (prm.indexOf('pr_') > -1));
        removeParamFromUrl(prParams);
      }
      const hfHotelId = getParamFromUrl('hfHotelId');
      const hotelId = getParamFromUrl('hotelId');
      const destination = getParamFromUrl('destinations');

      if (hotelId) dispatch(fetchHotelById({
        id: hotelId,
        destinationIds: destination
      }));

      if (!hfHotelId && hotelId && typeof destination === 'number') {
        dispatch(fetchStaticHotelById({ id: hotelId, destination }));
        dispatch(setShowLoader(true));
      }
    }

    return () => {
      firstLoad.current = false;
    }
  }, [firstLoad])

  useEffect(() => {
    dispatch(
      setAppState({ showResults: (Object.keys(getParamsFromUrl()).length > 1 && !isEntryFlow() && !isEntryFlow('link-presets-welcome')) })
    );
  }, [location.hash, dispatch]);

  useEffect(() => {
    const welcomeBlockElement = document.querySelector('.welcome-block');

    if (!welcomeBlockElement) return;

    if (
      (location.hash !== previousLocationHash || window.location.hash.indexOf('wizardSkip=true') > -1) &&
      !lastChangedFilter &&
      filtersReady &&
      Object.keys(getParamsFromUrl()).length > 1
    ) {
      dispatch(updateMainFilterFromUrl());
      dispatch(updateSecondaryFilterFromUrl());
    }

    if (showResults) {
      welcomeBlockElement.classList.add('hidden');
    } else {
      welcomeBlockElement.classList.remove('hidden');
      dispatch(clearDeals());
    }
  }, [dispatch, filtersReady, lastChangedFilter, showResults]);

  // refactor getting params from url for new search reducer.
  useEffect(() => {
    const filters = getParamsFromUrl();

    const payload = {
      fromwhere: filters.fromwhere,
      what: [],
      where: {
        destinations: [],
        destinationsGroups: [],
      },
      when: getFormattedWhen(filters),
      who: {
        adults: filters.adults || 2,
        children: filters.children || 0,
        rooms: filters.rooms || 1,
        childAges: [],
      },
      budget: filters.budget || [100, 3000],
    };

    if (filters.what) {
      payload.what = filters.what?.length ? filters.what : [filters.what];
    }

    if (filters.destinations) {
      payload.where.destinations = filters.destinations?.length ? filters.destinations : [filters.destinations];
    }

    if (filters.destinationsGroups) {
      payload.where.destinationsGroups = filters.destinationsGroups?.length ? filters.destinationsGroups : [filters.destinationsGroups];
    }

    if (filters.childAges) {
      payload.who.childAges = filters.childAges?.length ? filters.childAges : [filters.childAges];
    }

    dispatch(updateSearch(payload));
  }, [dispatch])
}
